/**
 * Alsunu — product offer cards.
 *
 * Three jobs, in order of how often they run:
 *
 *  1. Selection state. The radios are real inputs, so keyboard and screen
 *     readers work without help; this only mirrors the checked state onto the
 *     card for styling and announces the change.
 *
 *  2. Quantity. A tiered offer ("Two Sets") owns the quantity, so the selection
 *     is written into the product form's quantity input and the manual stepper
 *     is hidden — two controls fighting over the same number is how carts end
 *     up with five of something.
 *
 *  3. Extra lines. Offers that carry additional products or a gift need more
 *     than one line item. Rather than replace Horizon's submit handler, the
 *     extras are posted to /cart/add.js first and the original submit is then
 *     replayed, so the theme still adds the main line, refreshes its sections
 *     and opens the drawer exactly as it does on every other product.
 *
 * Prices are never calculated here. What the card shows is rendered server-side
 * from live variant prices, and the money actually taken off is a Shopify
 * automatic discount applied in the cart — this file moves line items, nothing
 * more.
 */
import { StandardEvents } from '@shopify/events';

(() => {
  if (customElements.get('als-bundle-offers')) return;

  const ADD_URL = () => (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  /** Remembers the chosen card so a variant change doesn't silently reset it. */
  const lastChoice = new Map();

  class AlsBundleOffers extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-als-offer]'));
      if (!this.cards.length) return;

      this.form = this.closest('.product-details, .shopify-section')?.querySelector('form[data-type="add-to-cart-form"]');
      this.status = this.querySelector('[data-als-offer-status]');
      this.errorEl = this.querySelector('[data-als-offer-error]');
      this.sectionId = this.dataset.sectionId || 'default';
      this.busy = false;
      this.section = this.closest('.shopify-section');
      this.section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);

      this.#restoreChoice();
      this.#hideStepperIfTiered();

      this.addEventListener('change', this.#onChange);
      // The label covers the title and price, but the rows beneath it — savings,
      // bundle contents, the gift — were dead to the pointer. The whole card now
      // selects, while the controls inside it keep their own behaviour.
      this.addEventListener('click', this.#onCardClick);
      // Click, not submit. Horizon's submit handler is registered before this
      // deferred script runs, so stopping the submit event is a race this file
      // loses — and losing it adds the main line twice. Cancelling the click
      // means the submit is never dispatched at all; it is re-issued below once
      // the companion lines are in.
      document.addEventListener('click', this.#onAddClick, { capture: true });

      this.#sync();

      // Pause the decorative badge/gift motion while the cards are off screen.
      if ('IntersectionObserver' in window) {
        this.visibility = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-offscreen', !entry.isIntersecting);
        });
        this.visibility.observe(this);
      }
    }

    disconnectedCallback() {
      this.visibility?.disconnect();
      this.removeEventListener('change', this.#onChange);
      this.removeEventListener('click', this.#onCardClick);
      document.removeEventListener('click', this.#onAddClick, { capture: true });
      this.section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    }

    /* ---------------------------------------------------------------- state */

    // Horizon updates individual components after a variant fetch. Subscribe to
    // that same response so offer images, names and server-calculated prices
    // stay current without another request or client-side discount arithmetic.
    #onProductSelect = (event) => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
      const pending = event.promise;
      this.variantRequest = pending;
      pending?.then(({ detail }) => {
        if (!this.isConnected || this.variantRequest !== pending || !detail?.html) return;
        const next = Array.from(detail.html.querySelectorAll('als-bundle-offers'))
          .find(el => el.dataset.sectionId === this.sectionId);
        if (!next) return;
        const replacement = next.cloneNode(true);
        const oldSelects = this.querySelectorAll('select[data-als-extra], select[data-als-gift], select[data-als-unit]');
        const newSelects = replacement.querySelectorAll('select[data-als-extra], select[data-als-gift], select[data-als-unit]');
        for (let i = 0; i < oldSelects.length; i++) {
          const select = newSelects[i];
          if (!select || !Array.from(select.options).some(option => option.value === oldSelects[i].value)) continue;
          select.value = oldSelects[i].value;
          const imageUrl = select.selectedOptions[0]?.dataset.image;
          const image = select.closest('.als-offer__item')?.querySelector('img.als-offer__thumb');
          if (image && imageUrl) {
            image.removeAttribute('srcset');
            image.src = imageUrl;
          }
        }
        this.replaceWith(replacement);
      }).catch(() => {
        // Aborted requests are expected when a shopper changes color quickly.
        // Horizon handles failed variant requests and purchase availability.
      });
    };

    get selectedCard() {
      return this.cards.find((card) => card.querySelector('[data-als-offer-input]')?.checked) || null;
    }

    #restoreChoice = () => {
      const remembered = lastChoice.get(this.sectionId);
      if (remembered == null) return;
      const input = this.cards[remembered]?.querySelector('[data-als-offer-input]');
      if (input) input.checked = true;
    };

    #onCardClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('label, select, option, a, button, input')) return;

      const card = target.closest('[data-als-offer]');
      const input = card?.querySelector('[data-als-offer-input]');
      if (!input || input.checked) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    #onChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (target.matches('select[data-als-extra], select[data-als-gift]')) {
        const imageUrl = target.selectedOptions[0]?.dataset.image;
        const image = target.closest('.als-offer__item')?.querySelector('img.als-offer__thumb');
        if (image && imageUrl) {
          image.removeAttribute('srcset');
          image.src = imageUrl;
        }
      }

      // A unit's option changes how many go on the main line.
      if (target.matches('select[data-als-unit]')) {
        this.#sync();
        return;
      }

      // A variant <select> inside a card shouldn't change which card is chosen.
      if (target.matches('[data-als-offer-input]')) {
        lastChoice.set(this.sectionId, this.cards.indexOf(target.closest('[data-als-offer]')));
        this.#sync();
      }
    };

    #sync() {
      const selected = this.selectedCard;

      for (const card of this.cards) {
        const on = card === selected;
        card.toggleAttribute('data-selected', on);
        // Controls inside an unselected card are still reachable, but they are
        // not part of the purchase until that card is chosen.
        card.querySelectorAll('select[data-als-extra], select[data-als-gift], select[data-als-unit]').forEach((el) => {
          el.disabled = !on;
        });
      }

      if (!selected) return;

      // Units left on the page's option ride on the main line; units switched
      // to another option become their own lines (see #extraLines).
      const quantity = Number(selected.dataset.quantity) || 1;
      const mainId = this.#mainVariantId();
      const units = Array.from(selected.querySelectorAll('select[data-als-unit]'));
      const moved = units.filter((el) => Number(el.value) !== mainId).length;
      this.#setFormQuantity(Math.max(1, quantity - moved));

      if (this.status) {
        const title = selected.querySelector('.als-offer__title')?.textContent?.trim() || '';
        // Read the figure from the attribute: the visible node also carries a
        // visually-hidden "Offer price" prefix, which would be spoken twice.
        const priceEl = selected.querySelector('.als-offer__price');
        const price = (priceEl?.dataset.price || priceEl?.textContent || '').trim();
        // Clear first: assistive tech skips an update whose text is identical to
        // what the region already holds, which happens whenever a customer
        // returns to a card they had selected before.
        this.status.textContent = '';
        if (title && price) {
          const message = `${title} selected. Total ${price}.`;
          requestAnimationFrame(() => {
            this.status.textContent = message;
          });
        }
      }
      this.#clearError();
    }

    #setFormQuantity(quantity) {
      const input = this.form?.querySelector('input[name="quantity"]');
      if (!input || Number(input.value) === quantity) return;
      input.value = String(quantity);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * When any offer buys more than one, the offer cards are the quantity
     * control and the stepper would only contradict them.
     */
    #hideStepperIfTiered() {
      const tiered = this.cards.some((card) => Number(card.dataset.quantity) > 1);
      if (!tiered || !this.form) return;
      const wrapper = this.form.querySelector('.quantity-selector-wrapper');
      if (wrapper) wrapper.setAttribute('data-als-offer-managed', 'true');
    }

    /* ----------------------------------------------------------- extra lines */

    /** @returns {{id:number, quantity:number}[]} */
    #extraLines() {
      const selected = this.selectedCard;
      if (!selected) return [];

      const lines = [];
      selected.querySelectorAll('[data-als-extra], [data-als-gift]').forEach((el) => {
        const id = Number(el.value);
        if (Number.isFinite(id) && id > 0) lines.push({ id, quantity: 1 });
      });
      const mainId = this.#mainVariantId();
      selected.querySelectorAll('select[data-als-unit]').forEach((el) => {
        const id = Number(el.value);
        if (!Number.isFinite(id) || id <= 0 || id === mainId) return;
        const same = lines.find((line) => line.id === id);
        if (same) same.quantity += 1;
        else lines.push({ id, quantity: 1 });
      });
      return lines;
    }

    #mainVariantId() {
      return Number(this.form?.querySelector('input[name="id"]')?.value) || 0;
    }

    #onAddClick = (event) => {
      if (this.busy || !this.form) return;

      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('button[name="add"]');
      if (!button || button.disabled || button.form !== this.form) return;

      const lines = this.#extraLines();
      if (!lines.length) return;

      // Let the browser's own validation run before anything is added.
      if (typeof this.form.reportValidity === 'function' && !this.form.reportValidity()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitter = button;
      this.busy = true;

      fetch(ADD_URL(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines }),
      })
        .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) throw new Error(body?.description || body?.message || 'add_failed');
        })
        .catch(() => {
          // The main product still goes in; only the companion items failed.
          this.#showError('We couldn’t add the extra items. Your main item is in the cart — please try again.');
        })
        .finally(() => {
          this.#replay(submitter);
        });
    };

    #replay(submitter) {
      // `busy` stays true across the replay so this handler steps aside, then
      // resets once Horizon's own submit has been dispatched.
      if (typeof this.form.requestSubmit === 'function') {
        this.form.requestSubmit(submitter && submitter.form === this.form ? submitter : undefined);
      } else {
        this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
      window.setTimeout(() => {
        this.busy = false;
      }, 0);
    }

    #showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    #clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  customElements.define('als-bundle-offers', AlsBundleOffers);
})();